import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { JCONSULT_LOGO_MARK } from '../../config/brandingAssets';
import { isCohorteFull } from '../../utils/cohorteCapacityMessage';
import {
  formatDateFr,
  type PublicCohorteRow,
} from '../../utils/publicPlanningDisplay';

type Props = {
  formationLabel: string;
  coverImageUrl?: string | null;
  cohortes: PublicCohorteRow[];
};

function featuredCohorte(cohortes: PublicCohorteRow[]): PublicCohorteRow | null {
  if (cohortes.length === 0) return null;
  const withPlaces = cohortes.find((c) => !isCohorteFull(c));
  return withPlaces ?? cohortes[0] ?? null;
}

/** Somme des places restantes sur toutes les cohortes. */
function totalPlacesRestantes(cohortes: PublicCohorteRow[]): number {
  return cohortes.reduce((sum, cohorte) => {
    if (isCohorteFull(cohorte)) return sum;
    const places = cohorte.capacity?.placesRestantes;
    if (places != null) return sum + Math.max(0, places);
    const cap = cohorte.capacity?.capaciteEffective ?? cohorte.capaciteMax;
    return sum + (cap != null ? Math.max(0, cap) : 0);
  }, 0);
}

export default function PublicFiliereCohortesSessionCard({
  formationLabel,
  coverImageUrl,
  cohortes,
}: Props) {
  const featured = featuredCohorte(cohortes);
  const placesTotal = totalPlacesRestantes(cohortes);
  const allFull = cohortes.length > 0 && placesTotal <= 0;
  const placesLabel = String(placesTotal);
  const avatarSrc = coverImageUrl?.trim() || JCONSULT_LOGO_MARK;
  const title =
    featured?.libelle?.trim() ||
    featured?.code ||
    'Sessions';

  return (
    <div className="pfd-cohortes-session pricing-card" data-purpose="session-cohortes-card">
      <div className="pfd-cohortes-session__stage">
        {cohortes.length === 0 ? (
          <div className="pfd-cohortes-session__card pfd-cohortes-session__card--empty">
            <LucideSymbolIcon name="groups" tone="muted" size={28} className="mx-auto opacity-60" />
            <p className="pfd-cohortes-session__empty-title">Aucune cohorte disponible</p>
            <p className="pfd-cohortes-session__empty-text">
              Les prochaines dates de session seront publiées par l&apos;administration.
            </p>
            <div className="pfd-cohortes-session__actions">
              <a href="#details-formation" className="pfd-cohortes-session__btn pfd-cohortes-session__btn--solid">
                Détails
              </a>
            </div>
          </div>
        ) : (
          <>
            <div className="pfd-cohortes-session__card">
              <div className="pfd-cohortes-session__head">
                <p className="pfd-cohortes-session__price">
                  {placesLabel}
                  <span className="pfd-cohortes-session__price-unit">
                    {' '}
                    place{placesTotal > 1 ? 's' : ''}
                  </span>
                </p>
                <img
                  src={avatarSrc}
                  alt={formationLabel}
                  className="pfd-cohortes-session__avatar"
                  width={44}
                  height={44}
                />
              </div>

              <h2 className="pfd-cohortes-session__title">{title}</h2>

              <div className="pfd-cohortes-session__date-block">
                <p className="pfd-cohortes-session__date-label">Date</p>
                <div className="pfd-cohortes-session__date-rule" />
                <ul className="pfd-cohortes-session__dates">
                  {cohortes.map((cohorte) => {
                    const isFull = isCohorteFull(cohorte);
                    return (
                      <li key={cohorte.id} className="pfd-cohortes-session__date-row">
                        <div className="pfd-cohortes-session__date-main">
                          <span className="pfd-cohortes-session__date-code">{cohorte.code}</span>
                          <span className="pfd-cohortes-session__date-range">
                            Du {formatDateFr(cohorte.dateDebut)} au {formatDateFr(cohorte.dateFin)}
                          </span>
                        </div>
                        <span
                          className={`pfd-cohortes-session__date-badge ${
                            isFull
                              ? 'pfd-cohortes-session__date-badge--warn'
                              : 'pfd-cohortes-session__date-badge--ok'
                          }`}
                        >
                          {isFull ? 'Complet' : 'Ouverte'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="pfd-cohortes-session__actions">
                <a href="#details-formation" className="pfd-cohortes-session__btn pfd-cohortes-session__btn--solid">
                  Détails
                </a>
              </div>
            </div>

            <div className="pfd-cohortes-session__float">
              <span
                className={`pfd-cohortes-session__float-icon ${
                  allFull ? 'pfd-cohortes-session__float-icon--warn' : ''
                }`}
                aria-hidden
              >
                <LucideSymbolIcon name={allFull ? 'warning' : 'event_available'} tone="inherit" size={18} />
              </span>
              <div className="pfd-cohortes-session__float-copy">
                <p className="pfd-cohortes-session__float-label">
                  {allFull ? 'Sessions complètes' : 'Places restantes'}
                </p>
                <p className="pfd-cohortes-session__float-meta">
                  {cohortes.length} cohorte{cohortes.length > 1 ? 's' : ''}
                </p>
              </div>
              <p className="pfd-cohortes-session__float-value">{placesLabel}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
